import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const BASE = process.env.PUBLIC_URL || '';
const APP_STORE_URL = 'https://apps.apple.com/app/id955097868';
const APP_STORE_BADGE = `${BASE}/Download_on_the_App_Store_Badge_US.svg`;

const SCREENSHOTS = [
  { src: `${BASE}/clp2/0x0ss.png`, alt: 'Chain Link Pro 2 gameplay screenshot 1' },
  { src: `${BASE}/clp2/0x1ss.png`, alt: 'Chain Link Pro 2 gameplay screenshot 2' },
  { src: `${BASE}/clp2/0x2ss.png`, alt: 'Chain Link Pro 2 gameplay screenshot 3' },
  { src: `${BASE}/clp2/0x3ss.png`, alt: 'Chain Link Pro 2 gameplay screenshot 4' },
];

function HeroCarousel({ images }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(i => (i + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="hero-carousel">
      <div className="hero-carousel__frame">
        {images.map((img, i) => (
          <img
            key={i}
            src={img.src}
            alt={img.alt}
            className={`hero-carousel__img${i === current ? ' hero-carousel__img--active' : ''}`}
          />
        ))}
      </div>
      <div className="hero-carousel__dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`hero-carousel__dot${i === current ? ' hero-carousel__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`View screenshot ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const GAME_MODES = [
  {
    icon: '🔲',
    title: 'Static Mode',
    desc: "Shapes sit on a grid and don't move. Plan your chains carefully; you're not racing the clock here.",
  },
  {
    icon: '🌊',
    title: 'Arcade Mode',
    desc: 'Shapes flow in from the screen edges. Chain them fast before they pile up and overwhelm you.',
  },
  {
    icon: '⏱️',
    title: 'Timed Mode',
    desc: 'Beat the clock. Every chain you complete adds time; keep the combos flowing.',
  },
  {
    icon: '⚡',
    title: 'Hyper Chain',
    desc: '60-second sprint mode. How high can you score in one minute? Check the weekly leaderboard.',
  },
  {
    icon: '🔗',
    title: 'Chain Pattern',
    desc: 'Match the displayed sequence of shapes in the right order. Pattern recognition meets speed.',
  },
  {
    icon: '🌀',
    title: 'Chain Entity',
    desc: 'A growing shape mechanic returns with new twists. Navigate around obstacles as entities expand.',
  },
];

const NEW_IN_2 = [
  {
    icon: '🏆',
    title: 'Weekly Hyper Chain Leaderboard',
    desc: 'Compete for the top 5 spots on the Hyper Chain leaderboard that resets every week. A new challenge every Sunday.',
  },
  {
    icon: '⌚',
    title: 'Mystery Power-Up',
    desc: 'A new mystery power-up resets every 2 hours; log back in regularly to discover what\'s available.',
  },
  {
    icon: '⏳',
    title: 'Bonus Time Mechanics',
    desc: 'New time-bonus rules make Timed Mode even more dynamic and rewarding for skilled chaining.',
  },
  {
    icon: '💥',
    title: 'Enhanced Collision Effects',
    desc: 'Visual and audio collision feedback has been completely overhauled for a more satisfying feel.',
  },
  {
    icon: '🔓',
    title: 'Premium Unlock',
    desc: 'Upgrade to Premium once to instantly unlock all 17 power-ups (no grinding required).',
  },
  {
    icon: '🍎',
    title: 'Apple Watch Support',
    desc: 'Chain Link Pro 2 added an Apple Watch icon, keeping you connected to the game ecosystem.',
  },
];

const FEATURES = [
  {
    icon: '✨',
    title: '17 Power-Ups',
    desc: 'Miniature chains, multi-colored variants, metamorphosis, and chain invincibility. Unlock via quests or go Premium.',
  },
  {
    icon: '🎯',
    title: 'Quest System',
    desc: 'Complete in-game quests to progressively unlock power-ups and gain a sense of progression.',
  },
  {
    icon: '📈',
    title: 'Score Multipliers',
    desc: 'Speedster bonuses reward fast chaining. Repeater rewards keep the combos rolling.',
  },
  {
    icon: '🏆',
    title: 'Game Center',
    desc: 'Full leaderboard and achievement support. See how you stack up against players worldwide.',
  },
  {
    icon: '🎵',
    title: '3D Audio & Custom Music',
    desc: 'Enjoy immersive 3D sound effects with headphones, or play your own music in the background.',
  },
  {
    icon: '⚙️',
    title: 'Difficulty Levels',
    desc: 'Easy, medium, and hard settings plus auto-save and auto-pause for a seamless experience.',
  },
];

function useScrollAnimations() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = document.querySelectorAll('.animate-on-scroll');
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function ChainLinkPro2() {
  useScrollAnimations();

  return (
    <div className="detail-page">
      {/* ── Hero ── */}
      <section className="detail-hero detail-hero--clp2">
        <div className="hero__orb hero__orb--purple" style={{ opacity: 0.15 }} />
        <div
          className="hero__orb hero__orb--blue"
          style={{ opacity: 0.08, top: '60%', left: '60%' }}
        />

        <div className="detail-hero__inner">
          <div className="detail-hero__grid">
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'rgba(167,139,250,0.15)',
                  border: '1px solid rgba(167,139,250,0.35)',
                  borderRadius: '999px',
                  padding: '0.25rem 0.875rem',
                  marginBottom: '1rem',
                  color: '#c4b5fd',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                }}
              >
                ✦ The Long-Awaited Sequel to the Apple-Featured Original
              </div>

              <h1 className="detail-hero__title">Chain Link Pro 2</h1>
              <p className="detail-hero__desc">
                The sequel delivers everything fans loved about Chain Link Pro, and more.
                Six game modes, 17 power-ups, weekly leaderboards, a mystery power-up
                that refreshes every 2 hours, and enhanced collision effects make this
                the definitive chain puzzle experience.
              </p>

              <div className="detail-hero__download-btns">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={APP_STORE_BADGE} alt="Download on the App Store" className="btn-download-badge" />
                </a>
              </div>

              <div className="detail-hero__stats">
                <div className="detail-stat">
                  <div className="detail-stat__icon">🆓</div>
                  <div>
                    <div className="detail-stat__value">Free</div>
                    <div className="detail-stat__label">To Download</div>
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat__icon">🎮</div>
                  <div>
                    <div className="detail-stat__value">6</div>
                    <div className="detail-stat__label">Game Modes</div>
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat__icon">✨</div>
                  <div>
                    <div className="detail-stat__value">17</div>
                    <div className="detail-stat__label">Power-Ups</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero visual */}
            <div className="detail-hero__visual">
              <HeroCarousel images={SCREENSHOTS} />
              <div className="detail-hero__glow detail-hero__glow--clp2" />
            </div>
          </div>
        </div>

        <div className="hero__scroll-indicator">
          <div className="hero__scroll-dot" />
        </div>
      </section>

      {/* ── What's New in 2 ── */}
      <section className="detail-features">
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            What's New in <span className="gradient-text">Chain Link Pro 2</span>
          </h2>
          <p className="section-subtitle">
            The sequel builds on everything that made the original great, with
            exciting new features for returning fans and newcomers alike.
          </p>
        </div>

        <div className="features-grid">
          {NEW_IN_2.map((f, i) => (
            <div
              key={i}
              className={`feature-card animate-on-scroll stagger-${(i % 3) + 1}`}
            >
              <div className="feature-card__icon feature-card__icon--clp2">
                {f.icon}
              </div>
              <h3 className="feature-card__title">{f.title}</h3>
              <p className="feature-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Game Modes ── */}
      <section className="detail-features" style={{ background: '#f8fafc' }}>
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            6 Game <span className="gradient-text">Modes</span>
          </h2>
          <p className="section-subtitle">
            Six distinct ways to play, each with its own strategy and challenge.
          </p>
        </div>

        <div className="features-grid">
          {GAME_MODES.map((m, i) => (
            <div
              key={i}
              className={`feature-card animate-on-scroll stagger-${(i % 3) + 1}`}
            >
              <div className="feature-card__icon feature-card__icon--clp2">
                {m.icon}
              </div>
              <h3 className="feature-card__title">{m.title}</h3>
              <p className="feature-card__desc">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Core Features ── */}
      <section className="detail-features">
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            Core <span className="gradient-text">Features</span>
          </h2>
          <p className="section-subtitle">
            Everything from the original: refined and expanded.
          </p>
        </div>

        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className={`feature-card animate-on-scroll stagger-${(i % 3) + 1}`}
            >
              <div className="feature-card__icon feature-card__icon--clp2">
                {f.icon}
              </div>
              <h3 className="feature-card__title">{f.title}</h3>
              <p className="feature-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="benefits-section">
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            Why Play <span className="gradient-text">Chain Link Pro 2</span>
          </h2>
        </div>

        <div className="benefits-grid">
          <div className="benefit-card benefit-card--purple animate-on-scroll stagger-1">
            <div className="benefit-card__icon benefit-card__icon--purple">🔁</div>
            <h3 className="benefit-card__title">Always Something New</h3>
            <p className="benefit-card__desc">
              Weekly leaderboards reset every Sunday and the mystery power-up refreshes
              every 2 hours; there's always a reason to return.
            </p>
          </div>
          <div className="benefit-card benefit-card--blue animate-on-scroll stagger-2">
            <div className="benefit-card__icon benefit-card__icon--blue">🆓</div>
            <h3 className="benefit-card__title">Free to Start</h3>
            <p className="benefit-card__desc">
              Download for free and experience all 6 game modes. Upgrade to Premium
              once to instantly unlock all 17 power-ups.
            </p>
          </div>
          <div className="benefit-card benefit-card--amber animate-on-scroll stagger-3">
            <div className="benefit-card__icon benefit-card__icon--amber">🏆</div>
            <h3 className="benefit-card__title">Compete Globally</h3>
            <p className="benefit-card__desc">
              Game Center achievements and leaderboards let you measure your skills
              against chain puzzle players around the world.
            </p>
          </div>
        </div>
      </section>

      {/* ── In-App Purchases ── */}
      <section style={{ padding: '4rem 1.5rem', background: '#f8fafc' }}>
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            In-App <span className="gradient-text">Purchases</span>
          </h2>
          <p className="section-subtitle">
            Free to download. Optional purchases to enhance your experience.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            maxWidth: 900,
            margin: '0 auto',
          }}
        >
          {[
            { name: '5 Extra Lives', price: '$0.99' },
            { name: 'Unlock All Power-Ups', price: '$0.99' },
            { name: 'Upgrade to Premium', price: '$1.99' },
          ].map((iap) => (
            <div
              key={iap.name}
              className="animate-on-scroll"
              style={{
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 12,
                padding: '1.25rem 1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9375rem' }}>
                {iap.name}
              </span>
              <span
                style={{
                  fontWeight: 700,
                  color: '#7c3aed',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap',
                  marginLeft: '1rem',
                }}
              >
                {iap.price}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="detail-cta detail-cta--clp2">
        <div className="detail-cta__orb" style={{ background: 'rgba(168,85,247,0.15)' }} />
        <div className="detail-cta__inner">
          <h2 className="detail-cta__title">
            The Chain Puzzle Sequel You've Been Waiting For
          </h2>
          <p className="detail-cta__subtitle">
            Download Chain Link Pro 2 for free today and discover why the original was
            Apple-featured; then experience everything the sequel adds.
          </p>
          <div className="detail-cta__btns">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={APP_STORE_BADGE} alt="Download on the App Store" className="btn-download-badge" />
            </a>
            <Link to="/apps/chain-link-pro" className="btn-download btn-download--outline">
              ← Try the Original First
            </Link>
          </div>
          <div className="app-page-footer">
            <span>© {new Date().getFullYear()} Software Beyond Limits. All rights reserved.</span>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ChainLinkPro2;
